import React from 'react';
import { connect } from 'react-redux';
import { withOktaAuth } from '@okta/okta-react';
import { Redirect } from 'react-router-dom';
import { setPageTitleAction } from '../actions/actions';
import { DrcMain, DrcPanel, DrcLoading, DrcProgress, DrcButton, DrcImage, DrcTranslations } from 'driscolls-react-components';
import MasterDataUtilities from '../data/MasterDataUtilities';
import { setIsAdmin, setIsReadOnly } from '../actions/userActions';
import { setTranslationRules, setPreferredLanguage } from '../actions/translationActions';
import {
    setLanguageOptions,
    setMasterDataInitialized,
    setDataTypeList,
    setAttributeOptionsList,
    setEntityOptionsList,
    setGroups
} from '../actions/MasterActions';
import { setValidationData } from '../actions/importFileActions';
import { DuAuthenticationUtilities, DuCommonTranslations } from 'driscolls-react-utilities';
import { hasEditAccess } from '../utils/helpers';
import { withLocalize } from 'react-localize-redux';
import { renderToStaticMarkup } from 'react-dom/server';
import FailPng from '../assets/fail.png';
import FailWebP from '../assets/fail.webp';

const pageTitle = 'Initializing';

const admins = (window.config.OKTA_APP_ADMIN || []).concat(
    window.config.OKTA_RD_ADMIN_GROUPS || [],
    window.config.OKTA_RD_ENTITY_ADMIN || [],
    window.config.OKTA_RD_READ_ONLY_GROUPS || [],
    window.config.OKTA_RD_REGULAR_GROUPS || [],
    window.config.OKTA_RD_ENTITY_ADMIN || [],
    window.config.OKTA_MDM_WAREHOUSE_ADMIN || []
    
);

class InitializeApplication extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            isLoaded: false,
            message: '',
            successCount: 0,
            errorCount: 0,
            totalCount: 0
        };

        this.statusUpdate = this.statusUpdate.bind(this);
        this.loadMasterData = this.loadMasterData.bind(this);
    }

    statusUpdate(message, successCount, errorCount, totalCount) {
        this.setState({ message, successCount, errorCount, totalCount });
    }

    async loadMasterData() {
        var token = await this.props.authService.getAccessToken();

        this.props.setIsAdmin(DuAuthenticationUtilities.IsInGroup(token, admins || []));
        this.props.setIsReadOnly(!hasEditAccess(token));

        MasterDataUtilities.Load(
            this.props,
            token,
            this.statusUpdate,
            (message) => {
                this.setState({ message });
                this.props.setMasterDataInitialized(true);
            },
            (error) => {
                console.error(error);
            }
        );
    }

    async componentDidUpdate(prevProps) {
        if (prevProps.translations !== this.props.translations) {
            let preferredLanguageCode = localStorage.getItem('preferredLanguageCode');
            if (!preferredLanguageCode) {
                let token = await this.props.authService.getAccessToken();
                let decodedToken = DuAuthenticationUtilities.DecodeToken(token);
                let language = decodedToken.lan;

                if (language && language.toLowerCase() === 'Spanish'.toLowerCase()) {
                    preferredLanguageCode = 'es';
                } else {
                    preferredLanguageCode = 'en';
                }
            }

            this.props.initialize({
                languages: DuCommonTranslations.LANGUAGES,
                translation: {
                    ...DuCommonTranslations.COMMON_TRANSLATIONS,
                    ...DrcTranslations.DRC_PAGE_DATA_MAINTENANCE_TRANSLATIONS,
                    ...this.props.translations
                },
                options: { renderToStaticMarkup, defaultLanguage: preferredLanguageCode }
            });
        }
    }

    componentDidMount() {
        //TODO: Need way to turn off header navigation so we can stay here till we have the master data
        if (this.props.pageTitle !== pageTitle) {
            this.props.setPageTitle(pageTitle);
        }

        this.loadMasterData();
    }

    render() {
        const { redirectUrl, isMasterDataInitialized } = this.props;
        var percentComplete = Math.ceil(((this.state.successCount + this.state.errorCount) / this.state.totalCount) * 100);

        return isMasterDataInitialized ? (
            <Redirect to={redirectUrl} />
        ) : (
            <DrcMain transparent>
                <DrcPanel maxWidth="500px">
                    {this.state.successCount + this.state.errorCount === this.state.totalCount && this.state.errorCount > 0 ? (
                        <DrcImage
                            src={FailPng}
                            webp={FailWebP}
                            style={{ position: 'absolute', height: 100, marginTop: -10, left: 'calc(50vw - 87px)' }}
                            alt="Thor and Callie are sorry they couldn't get the data you needed"
                        />
                    ) : null}
                    <DrcLoading
                        text={<h1>{this.state.message}</h1>}
                        hidden={this.state.successCount + this.state.errorCount === this.state.totalCount}
                    />
                    <DrcProgress value={percentComplete} />
                    {this.state.successCount + this.state.errorCount >= this.state.totalCount && this.state.errorCount > 0 ? (
                        <React.Fragment>
                            <hr />
                            <p>
                                We were unable to load {this.state.errorCount} out of {this.state.totalCount} important components for the site to
                                load properly. You may want to refresh the website or you can proceed with using the application knowing that some
                                aspects may not be working.
                            </p>
                            <hr />
                            <div className="row">
                                <div className="col-xs-6">
                                    <DrcButton
                                        poly
                                        line
                                        isPrimary
                                        fullWidth
                                        style={{ margin: '8px 0' }}
                                        onClick={() => {
                                            window.location.reload();
                                        }}
                                    >
                                        Try, Try Again
                                    </DrcButton>
                                </div>
                                <div className="col-xs-6">
                                    <DrcButton
                                        poly
                                        line
                                        isSecondary
                                        fullWidth
                                        style={{ margin: '8px 0' }}
                                        onClick={() => {
                                            this.props.setMasterDataInitialized(true);
                                        }}
                                    >
                                        Proceed to Website
                                    </DrcButton>
                                </div>
                            </div>
                        </React.Fragment>
                    ) : null}
                </DrcPanel>
            </DrcMain>
        );
    }
}

function mapStateToProps(state) {
    return {
        showLoadingScreen: state.rootReducer.showLoadingScreen,
        errorDialog: state.rootReducer.errorDialog,
        pageTitle: state.rootReducer.pageTitle,
        redirectUrl: state.masterReducer.redirectUrl,
        isMasterDataInitialized: state.masterReducer.isInitialized,
        translations: state.translationReducer.rules
    };
}

const mapDispatchToProps = (dispatch) => ({
    setPageTitle: (title) => dispatch(setPageTitleAction(title)),
    setMasterDataInitialized: (isInitialized) => dispatch(setMasterDataInitialized(isInitialized)),
    setLanguageOptions: (data) => dispatch(setLanguageOptions(data)),
    setIsAdmin: (data) => dispatch(setIsAdmin(data)),
    setDataTypeList: (data) => dispatch(setDataTypeList(data)),
    setAttributeOptionsList: (data) => dispatch(setAttributeOptionsList(data)),
    setValidationData: (data) => dispatch(setValidationData(data)),
    setEntityOptionsList: (data) => dispatch(setEntityOptionsList(data)),
    setTranslationRules: (rules) => dispatch(setTranslationRules(rules)),
    setGroups: (data) => dispatch(setGroups(data)),
    setIsReadOnly: (data) => dispatch(setIsReadOnly(data)),
    setPreferredLanguage: (data) => dispatch(setPreferredLanguage(data))
});

export default withOktaAuth(connect(mapStateToProps, mapDispatchToProps)(withLocalize(InitializeApplication)));
